import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { getContributors } from '@atlassian/search-client/contributors';
import { type User, usersQuery } from '@atlassian/search-client/user/user';
import { mergeAndDedupeOptions } from '@atlassian/search-common/merge-and-dedupe-options';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	FetchedSelectFilterOption,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import {
	all1pProductKeys,
	all3pProductKeys,
	ProductKeys,
	type ProductKeys1P,
} from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getUrsProductKey } from '../get-urs-product-key';
import { loadRecommendedUsers } from '../utils/recommendedUser';

const MAX_SEARCHED_OPTIONS_RESULTS = 5;
// Each site still returns MAX_SEARCHED_OPTIONS_RESULTS, so five sites could otherwise surface 25.
const MAX_MULTI_SITE_SEARCHED_OPTIONS_RESULTS = 10;
export const CONTRIBUTOR_FILTER_QUERY_KEYWORDS: string[] = ['@', 'contributor:'];

// Filter out Trello for milestone 1 (QS-4733)
// Focus does not have contributor field
const productKeys1P: ProductKeys1P[] = all1pProductKeys.filter(
	(key) => key !== ProductKeys.Trello && key !== ProductKeys.Focus && key !== ProductKeys.Talent,
);

const fetchContributorOptionsForSite = async (
	query: string,
	config: CloudConfig,
	cloudId: string,
): Promise<FetchedSelectFilterOption[]> => {
	if (!config.intl) {
		return [];
	}

	const response = await getContributors({
		query,
		cloudId,
		userId: config.userId || '',
		productKey: getUrsProductKey(config.primaryProduct),
		maxNumberOfResults: MAX_SEARCHED_OPTIONS_RESULTS,
		intl: config.intl,
	});

	if (!response) {
		// TODO: log not found somewhere
		return [];
	}

	return response.map((contributor) => ({
		trackingKey: contributor.id,
		value: contributor.id,
		label: contributor.name,
		avatarUrl: contributor.avatarUrl,
		queryParamValue: contributor.id,
		product: config.product,
	}));
};

// Figma doesn't support contributor
// https://atlassian.slack.com/archives/C07P3MD2U9Z/p1733868616441159?thread_ts=1733866616.102409&cid=C07P3MD2U9Z
const all3pProductKeysExceptFigma = all3pProductKeys.filter((key) => key !== ProductKeys3P.Figma);

export const filter: Readonly<{
	id: 'contributor';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'contributor'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'contributor'>;
	getInitialState: (
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'contributor'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'contributor',
	// 1P only for now (excluding Trello)
	products: [...productKeys1P, ...all3pProductKeysExceptFigma],
	alwaysVisible: true,
	universal: true,
	queryParams: {
		key: 'contributors',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const initialUsersReponse = await usersQuery([...queryParams], headers);

			const allRecommendedUsers = initialUsersReponse.data?.users ?? ([] satisfies User[]);

			const recommendedUserOptions = allRecommendedUsers.map((user) => ({
				trackingKey: user.accountId,
				value: user.accountId,
				label: user.name,
				avatarUrl: user.picture,
				queryParamValue: user.accountId,
				product: config.product,
			}));

			return recommendedUserOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return loadRecommendedUsers(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			if (!config.intl) {
				return []; // base on existing interface, can only query contributors with intl
			}

			const cloudIds = config.selectedCloudIds?.length
				? config.selectedCloudIds
				: [config.cloudId || ''];

			if (cloudIds.length === 1) {
				return fetchContributorOptionsForSite(query, config, cloudIds[0]);
			}

			const settledPerSite = await Promise.allSettled(
				cloudIds.map((cloudId) => fetchContributorOptionsForSite(query, config, cloudId)),
			);
			const succeeded = settledPerSite.filter(
				(result): result is PromiseFulfilledResult<FetchedSelectFilterOption[]> =>
					result.status === 'fulfilled',
			);

			// One unhealthy site should not blank out the whole filter; only fail if every site failed.
			if (succeeded.length === 0) {
				throw (settledPerSite[0] as PromiseRejectedResult).reason;
			}

			return mergeAndDedupeOptions(succeeded.map((result) => result.value)).slice(
				0,
				MAX_MULTI_SITE_SEARCHED_OPTIONS_RESULTS,
			);
		},
	},
});
